import React, { useState } from 'react';
import { Table, Button, Badge, Spinner } from 'react-bootstrap';
import EditStartDateModal from './EditStartDateModal';
import ConfirmEndProgramModal from './ConfirmEndProgramModal';
import SkipDaysModal from './SkipDaysModal';
import SkipWorkoutModal from './SkipWorkoutModal';
import WorkoutCalendar from './WorkoutCalendar';

function ActiveProgramsTable({ activePrograms, onContinue, onRefresh }) {
  const [editingProgram, setEditingProgram] = useState(null);
  const [endingProgram, setEndingProgram] = useState(null);
  const [skippingProgram, setSkippingProgram] = useState(null);
  const [skippingWorkout, setSkippingWorkout] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);

  // Format scheduled date for display
  const formatScheduledDate = (dateString) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString + 'T00:00:00'); // Force local timezone
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const scheduledDate = new Date(date);
    scheduledDate.setHours(0, 0, 0, 0);

    if (scheduledDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (scheduledDate.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else if (scheduledDate < today) {
      const daysLate = Math.floor((today - scheduledDate) / (1000 * 60 * 60 * 24));
      return `${daysLate} day${daysLate > 1 ? 's' : ''} ago`;
    } else {
      // Future date - show day and date in short format (e.g., "Mon, Jan 7")
      const options = { weekday: 'short', month: 'short', day: 'numeric' };
      return date.toLocaleDateString('en-US', options);
    }
  };

  const handleEditStartDate = (program) => {
    setEditingProgram(program);
  };

  const handleEndProgram = (program) => {
    setEndingProgram(program);
  };

  const handleSkipDays = (program) => {
    setSkippingProgram(program);
  };

  const handleSkipWorkout = (program) => {
    setSkippingWorkout(program);
  };

  const handleContinueClick = async (program) => {
    setIsLoading(true);
    try {
      await onContinue(program);
    } catch (err) {
      console.error('Error continuing program:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (activePrograms.length === 0) {
    return (
      <div className="text-center py-4 text-muted">
        <i className="fas fa-calendar-week fa-3x mb-3"></i>
        <p>No active programs. Start a program from the Programs section!</p>
      </div>
    );
  }

  // Show calendar view
  if (showCalendar) {
    return (
      <WorkoutCalendar
        activePrograms={activePrograms}
        onClose={() => {
          setShowCalendar(false);
          onRefresh(); // Refresh data when closing calendar
        }}
      />
    );
  }

  return (
    <>
      {/* Calendar View Button */}
      <div className="mb-3">
        <Button
          variant="outline-primary"
          onClick={() => setShowCalendar(true)}
        >
          <i className="fas fa-calendar-alt me-2"></i>
          Calendar View
        </Button>
      </div>

      <div className="table-responsive">
        <Table hover className="active-programs-table">
          <thead>
            <tr>
              <th>Program Name</th>
              <th>Current Progress</th>
              <th>Next Workout</th>
              <th>Scheduled Date</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {activePrograms.map((ap) => (
              <tr key={ap.id}>
                <td>
                  <strong>{ap.program_name}</strong>
                  <br />
                  <small className="text-muted">
                    Started: {new Date(ap.program_start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </small>
                </td>
                <td>
                  {ap.progress_label === 'Not started' ? (
                    <Badge bg="secondary">Not Started</Badge>
                  ) : (
                    <span>{ap.progress_label}</span>
                  )}
                </td>
                <td>
                  {ap.is_complete ? (
                    <Badge bg="success">
                      <i className="fas fa-check-circle me-1"></i>
                      Complete
                    </Badge>
                  ) : (
                    <>
                      <div>{ap.next_workout_name}</div>
                      {ap.next_workout_day_label && (
                        <small className="text-muted">{ap.next_workout_day_label}</small>
                      )}
                    </>
                  )}
                </td>
                <td>
                  {ap.is_complete ? (
                    <span className="text-muted">—</span>
                  ) : (
                    <span className={ap.next_workout_date && new Date(ap.next_workout_date) < new Date() ? 'text-danger fw-bold' : ''}>
                      {formatScheduledDate(ap.next_workout_date)}
                    </span>
                  )}
                </td>
                <td className="text-end">
                  <div className="btn-group" role="group">
                    {!ap.is_complete && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleContinueClick(ap)}
                        disabled={isLoading}
                      >
                        <i className="fas fa-play-circle me-1"></i>
                        Continue
                      </Button>
                    )}
                    {!ap.is_complete && (
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => handleSkipWorkout(ap)}
                        title="Skip this workout and move to next"
                      >
                        <i className="fas fa-step-forward"></i>
                      </Button>
                    )}
                    {!ap.is_complete && (
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => handleSkipDays(ap)}
                        title="Skip days (shift schedule forward)"
                      >
                        <i className="fas fa-forward"></i>
                      </Button>
                    )}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleEditStartDate(ap)}
                      title="Edit start date"
                    >
                      <i className="fas fa-calendar-alt"></i>
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleEndProgram(ap)}
                      title="End program"
                    >
                      <i className="fas fa-stop-circle"></i>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {editingProgram && (
        <EditStartDateModal
          program={editingProgram}
          onClose={() => setEditingProgram(null)}
          onSave={onRefresh}
        />
      )}

      {endingProgram && (
        <ConfirmEndProgramModal
          program={endingProgram}
          onClose={() => setEndingProgram(null)}
          onConfirm={onRefresh}
        />
      )}

      {skippingProgram && (
        <SkipDaysModal
          program={skippingProgram}
          onClose={() => setSkippingProgram(null)}
          onSuccess={onRefresh}
        />
      )}

      {skippingWorkout && (
        <SkipWorkoutModal
          program={skippingWorkout}
          nextWorkout={{
            next_workout_id: skippingWorkout.next_workout_id,
            next_workout_name: skippingWorkout.next_workout_name
          }}
          onClose={() => setSkippingWorkout(null)}
          onSuccess={onRefresh}
        />
      )}
    </>
  );
}

export default ActiveProgramsTable;
